/**
 * Terms of Service Page
 * 
 * Legal terms and conditions for WordExpress
 * Last Updated: February 6, 2026
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';

const LegalSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
}));

const TermsOfService = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            Terms of Service
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Last Updated: February 6, 2026
          </Typography>
        </Container>
      </Box>

      {/* Terms Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            1. Agreement to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing and using WordExpress ("Service", "Platform", "we", "us", or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
          </Typography>
          <Typography variant="body1">
            WordExpress is a WordPress theme generation platform that uses artificial intelligence to create custom themes. These Terms govern your use of our website, services, and any themes generated through our platform.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            2. Service Description
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>2.1 Theme Generation:</strong> WordExpress provides two primary services:
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • <strong>Quick Themes:</strong> Template-based themes with multiple variations and color schemes
            <br />
            • <strong>Custom Themes:</strong> Fully customized themes generated based on your specifications
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>2.2 Generation Process:</strong> Theme generation typically takes 5-15 minutes. Generation times may vary based on complexity and system load.
          </Typography>
          <Typography variant="body1">
            <strong>2.3 Delivery:</strong> Successfully generated themes are delivered as downloadable ZIP files containing WordPress theme files.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            3. Pay-On-Completion Model
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>3.1 Charging Policy:</strong> You will only be charged if your theme generates successfully and passes our validation checks. Failed generations result in zero charges.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>3.2 Free Retries:</strong> If generation fails, you receive up to 3 free retry attempts per theme. After 3 failed attempts, you may request a refund or contact support.
          </Typography>
          <Typography variant="body1">
            <strong>3.3 Validation:</strong> A theme is considered "successful" when it passes our automated validation checks, including file structure verification, code syntax checking, and WordPress compatibility testing.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            4. Pricing and Payment
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.1 Pricing:</strong> Current pricing is displayed on our Pricing page and may change with notice. Price changes do not affect pending or in-progress generations.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.2 Payment Processing:</strong> Payments are processed securely through Stripe. By providing payment information, you authorize us to charge your payment method for applicable fees.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.3 One-Time Charges:</strong> WordExPress uses pay-on-completion pricing. We do not auto-renew or charge recurring subscription fees for Quick or Custom theme generations.
          </Typography>
          <Typography variant="body1">
            <strong>4.4 Taxes:</strong> Prices do not include applicable taxes. You are responsible for any taxes, duties, or fees imposed by your jurisdiction.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            5. Refunds and Cancellations
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>5.1 Theme Refunds:</strong> If a generated theme has technical defects or fails to meet our quality standards, we will regenerate it for free (up to 3 attempts) or provide a full refund.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>5.2 No Recurring Billing:</strong> Because Quick and Custom themes are one-time purchases, there is no subscription cancellation process for standard theme generation.
          </Typography>
          <Typography variant="body1">
            <strong>5.3 Refund Requests:</strong> Refund requests must be submitted within 30 days of purchase. Contact support@wordexpress.com with your order details.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            6. Intellectual Property and Licensing
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>6.1 Theme Ownership:</strong> Upon successful payment, you receive a non-exclusive, royalty-free license to use the generated theme on your websites.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>6.2 Usage Rights:</strong> You may:
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • Use themes on unlimited websites you own or manage
            <br />
            • Modify and customize themes for your needs
            <br />
            • Use themes for commercial purposes
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>6.3 Restrictions:</strong> You may NOT:
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • Resell, redistribute, or sublicense themes as-is
            <br />
            • Claim themes as your original work
            <br />
            • Use themes for illegal purposes
          </Typography>
          <Typography variant="body1">
            <strong>6.4 Platform Rights:</strong> WordExpress retains all rights to the platform, AI technology, and generation process. Our trademarks, logos, and brand assets remain our property.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            7. User Accounts and Responsibilities
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.1 Account Creation:</strong> You must provide accurate, complete information when creating an account. You are responsible for maintaining account confidentiality.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.2 Acceptable Use:</strong> You agree not to:
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • Abuse, harass, or harm our staff or other users
            <br />
            • Attempt to reverse-engineer our AI or platform
            <br />
            • Use our service for illegal activities
            <br />
            • Overload our systems or attempt unauthorized access
            <br />
            • Generate themes for illegal, harmful, or adult content
          </Typography>
          <Typography variant="body1">
            <strong>7.3 Account Termination:</strong> We reserve the right to suspend or terminate accounts that violate these Terms or engage in abusive behavior.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            8. Warranties and Disclaimers
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.1 Service Availability:</strong> We strive for 99% uptime but do not guarantee uninterrupted service. Maintenance, updates, or technical issues may cause temporary unavailability.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.2 Theme Quality:</strong> While we implement quality checks, generated themes are provided "as is." We do not guarantee themes will meet all your specific requirements or be error-free.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.3 Third-Party Compatibility:</strong> We are not responsible for compatibility issues with third-party plugins, hosting providers, or custom WordPress configurations.
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            <strong>8.4 DISCLAIMER:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, WORDEXPRESS IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            9. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>9.1 Liability Cap:</strong> Our total liability for any claims related to the service is limited to the amount you paid us in the 12 months preceding the claim.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>9.2 Excluded Damages:</strong> We are not liable for indirect, incidental, consequential, or punitive damages, including lost profits, data loss, or business interruption.
          </Typography>
          <Typography variant="body1">
            <strong>9.3 Force Majeure:</strong> We are not liable for failures caused by circumstances beyond our reasonable control, including natural disasters, pandemics, or internet outages.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            10. Privacy and Data Protection
          </Typography>
          <Typography variant="body1" paragraph>
            Your use of WordExpress is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information. By using our Service, you consent to our data practices as described in the Privacy Policy.
          </Typography>
          <Typography variant="body1">
            We implement industry-standard security measures to protect your data, but no system is completely secure. You are responsible for maintaining the confidentiality of your login credentials.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            11. Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated "Last Updated" date. Continued use of the Service after changes constitutes acceptance of the modified Terms.
          </Typography>
          <Typography variant="body1">
            For material changes, we will provide notice via email or prominent platform notification at least 30 days before the changes take effect.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            12. Governing Law and Disputes
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>12.1 Governing Law:</strong> These Terms are governed by the laws of [Your State/Country], without regard to conflict of law provisions.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>12.2 Dispute Resolution:</strong> Any disputes shall first be attempted to be resolved through good-faith negotiation. If negotiation fails, disputes shall be resolved through binding arbitration.
          </Typography>
          <Typography variant="body1">
            <strong>12.3 Class Action Waiver:</strong> You agree to resolve disputes on an individual basis and waive the right to participate in class actions.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            13. Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            For questions about these Terms, please contact us:
          </Typography>
          <Typography variant="body1" component="div">
            <strong>Email:</strong> legal@wordexpress.com
            <br />
            <strong>Support:</strong> support@wordexpress.com
            <br />
            <strong>Website:</strong> www.wordexpress.com
          </Typography>
        </LegalSection>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="textSecondary">
            By using WordExpress, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsOfService;
